import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ContentView from '~/components/ContentView';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { useFetchPublisherEmergencyReportByPublisherId } from '~/hooks/api/useFetchPublisherEmergencyReportByPublisherId';
import { PublisherEmergencyStatus } from '~/utils/publisher-emergency-status';
import messages from './messages';
import DrawerButton from '../DrawerButton';
import AcknowledgeButton from './AcknowledgeButton';
import ReportAsSafeButton from './ReportAsSaveButton';

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  alignItems: 'center',
  justifyItems: 'flex-start',
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(4),
}));

const TrafficLight = styled('div')<{ color: string; isActive?: boolean }>(
  ({ color, isActive = false }) => ({
    width: '75px',
    height: '75px',
    borderRadius: '99999999px',
    backgroundColor: color,
    opacity: isActive ? 1 : 0.3,
  })
);

type TrafficLightProps = {
  isActive?: boolean;
};
const RedLight = (props: TrafficLightProps) => {
  const theme = useTheme();
  return (
    <TrafficLight color={theme.palette.error.main} isActive={props.isActive} />
  );
};
const YellowLight = (props: TrafficLightProps) => {
  const theme = useTheme();
  return (
    <TrafficLight
      color={theme.palette.warning.main}
      isActive={props.isActive}
    />
  );
};
const GreenLight = (props: TrafficLightProps) => {
  const theme = useTheme();
  return (
    <TrafficLight
      color={theme.palette.success.main}
      isActive={props.isActive}
    />
  );
};

const EmergencyStatus = () => {
  const { data, isLoading } = usePublisherMe();
  const { data: emergencyModeData, isLoading: isLoadingEmergencyMode } =
    useFetchPublisherEmergencyReportByPublisherId(data?.id || 0, {
      enabled: !isLoading,
    });
  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <DrawerButton />
              </Grid>
              <Grid>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      }
      isLoading={isLoadingEmergencyMode}
    >
      {data && emergencyModeData ? (
        <Box sx={{ p: 2 }}>
          <StyledGrid>
            <RedLight
              isActive={
                emergencyModeData.report?.status ===
                PublisherEmergencyStatus.Unknown
              }
            />
            <div>
              {emergencyModeData.report?.status ===
              PublisherEmergencyStatus.Unknown ? (
                <FormattedMessage {...messages.unknown} />
              ) : null}
            </div>

            <YellowLight
              isActive={
                emergencyModeData.report?.status ===
                PublisherEmergencyStatus.Acknowledged
              }
            />
            <div>
              {emergencyModeData.report?.status ===
              PublisherEmergencyStatus.Acknowledged ? (
                <FormattedMessage {...messages.acknowledged} />
              ) : null}
              {emergencyModeData.report?.status ===
              PublisherEmergencyStatus.Unknown ? (
                <AcknowledgeButton publisherId={data.id} />
              ) : null}
            </div>

            <GreenLight
              isActive={
                emergencyModeData.report?.status ===
                PublisherEmergencyStatus.Safe
              }
            />
            <div>
              {emergencyModeData.report?.status ===
              PublisherEmergencyStatus.Safe ? (
                <FormattedMessage {...messages.safe} />
              ) : null}
              {emergencyModeData.report?.status ===
              PublisherEmergencyStatus.Acknowledged ? (
                <ReportAsSafeButton publisherId={data.id} />
              ) : null}
            </div>
          </StyledGrid>
        </Box>
      ) : null}
    </ContentView>
  );
};

export default EmergencyStatus;
